/*
  Zaktualizowano Store:
  1. Dodano obsługę nowego typu elementu "Brama garażowa" (GARAGE_DOOR).
  2. Ustawiono domyślną szerokość bramy garażowej na 250 cm.
*/
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { TOOLS, ELEMENT_TYPES, HATCH_PATTERNS } from '../core/constants';
import { getClosestWall, getPositionOnWall, getWallAngle, pointToLineDistance } from '../core/geometry';
import usePreferencesStore from './usePreferencesStore';

// Helper functions for polygon calculations
const calculatePolygonArea = (cornerIds, corners) => {
  const areaPoints = cornerIds.map(id => corners[id]).filter(Boolean);
  if (areaPoints.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < areaPoints.length; i++) {
    const j = (i + 1) % areaPoints.length;
    area += areaPoints[i].x * areaPoints[j].y;
    area -= areaPoints[j].x * areaPoints[i].y;
  }
  return Math.abs(area / 2) / 10000;
};

const calculatePolygonCenter = (cornerIds, corners) => {
  const areaPoints = cornerIds.map(id => corners[id]).filter(Boolean);
  if (areaPoints.length === 0) return { x: 0, y: 0 };
  const centerX = areaPoints.reduce((sum, point) => sum + point.x, 0) / areaPoints.length;
  const centerY = areaPoints.reduce((sum, point) => sum + point.y, 0) / areaPoints.length;
  return { x: centerX, y: centerY };
};

const useSceneStore = create(
  persist(
    (set, get) => ({
      projectName: 'Nowy Projekt',
      projectId: null,
      tool: TOOLS.SELECT,
      activeLayer: 'ground',
      scale: 1,
      position: { x: 0, y: 0 },
      drawing: false,
      activeWallId: null,
      activeSurfaceId: null,
      drawingStartPos: null,
      tempEnd: null,
      selectedCornerId: null,
      selectedWallId: null,
      selectedElementId: null,
      selectedRoomId: null,
      selectedSurfaceId: null,
      selectedDimensionId: null,
      isDraggingCorner: false,
      isDraggingWall: false,
      isDraggingElement: false,
      isDraggingSurface: false,
      isDraggingDimension: false,
      draggingSurfacePointIndex: null, 
      dragStartPos: null,
      dragStartData: null,
      rooms: {},
      surfaces: {},
      dimensionLines: {},
      corners: {},
      walls: {},
      elements: {},

      setProjectName: (name) => set({ projectName: name }),
      setProjectId: (id) => set({ projectId: id }),
      setTool: (tool) => set({ tool, drawing: false, activeWallId: null, activeSurfaceId: null, drawingStartPos: null, tempEnd: null }),
      clearSelection: () => set({ selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null, draggingSurfacePointIndex: null }),
      setStagePosition: (pos) => set({ position: pos }),
      setStageScale: (scale) => set({ scale }),
      selectCorner: (cornerId) => set({ selectedCornerId: cornerId, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null }),
      selectWall: (wallId) => set({ selectedWallId: wallId, selectedCornerId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null }),
      selectElement: (elementId) => set({ selectedElementId: elementId, selectedCornerId: null, selectedWallId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null }),
      selectRoom: (roomId) => set({ selectedRoomId: roomId, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedSurfaceId: null, selectedDimensionId: null }),
      selectSurface: (surfaceId) => set({ selectedSurfaceId: surfaceId, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedDimensionId: null }),
      selectDimension: (id) => set({ selectedDimensionId: id, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null }),

      bringToFront: (id, type) => {
        set((state) => {
           if (type === 'room') {
               const item = state.rooms[id];
               if (!item) return {};
               const newObj = { ...state.rooms };
               delete newObj[id];
               newObj[id] = item;
               return { rooms: newObj };
           }
           if (type === 'wall') {
               const item = state.walls[id];
               if (!item) return {};
               const newObj = { ...state.walls };
               delete newObj[id];
               newObj[id] = item;
               return { walls: newObj };
           }
           if (type === 'surface') {
               const item = state.surfaces[id];
               if (!item) return {};
               const newObj = { ...state.surfaces };
               delete newObj[id];
               newObj[id] = item;
               return { surfaces: newObj };
           }
           if (type === 'element') {
               const item = state.elements[id];
               if (!item) return {};
               const newObj = { ...state.elements };
               delete newObj[id];
               newObj[id] = item;
               return { elements: newObj };
           }
           return {};
        });
      },

      startDrawingDimension: (startPos) => {
        set({ drawing: true, tool: TOOLS.DIMENSION_LINE, drawingStartPos: startPos, tempEnd: startPos });
      },
      updateDrawingDimension: (currentPos) => {
        set({ tempEnd: currentPos });
      },
      finishDrawingDimension: (endPos) => {
        const { drawingStartPos, dimensionLines } = get();
        if (!drawingStartPos) return;
        const dist = Math.sqrt(Math.pow(endPos.x - drawingStartPos.x, 2) + Math.pow(endPos.y - drawingStartPos.y, 2));
        if (dist < 5) {
             set({ drawing: false, drawingStartPos: null, tempEnd: null });
             return;
        }
        const id = uuidv4();
        const newDimension = { id, start: drawingStartPos, end: endPos, customValue: null };
        set({ dimensionLines: { ...dimensionLines, [id]: newDimension }, drawing: false, drawingStartPos: null, tempEnd: null, selectedDimensionId: id });
      },
      updateDimensionValue: (id, value) => {
        set((state) => ({ dimensionLines: { ...state.dimensionLines, [id]: { ...state.dimensionLines[id], customValue: value } } }));
      },
      deleteDimension: (id) => {
        set((state) => {
          const newDimensions = { ...state.dimensionLines };
          delete newDimensions[id];
          return { dimensionLines: newDimensions, selectedDimensionId: null };
        });
      },
      startMovingDimension: (id) => {
        const dim = get().dimensionLines[id];
        if (!dim) return;
        set({ isDraggingDimension: true, selectedDimensionId: id, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, dragStartPos: { start: dim.start, end: dim.end } });
      },
      updateMovingDimension: (deltaX, deltaY) => {
         const { isDraggingDimension, selectedDimensionId, dimensionLines } = get();
         if (!isDraggingDimension || !selectedDimensionId) return;
         const dim = dimensionLines[selectedDimensionId];
         if (!dim) return;
         const newStart = { x: dim.start.x + deltaX, y: dim.start.y + deltaY };
         const newEnd = { x: dim.end.x + deltaX, y: dim.end.y + deltaY };
         set({ dimensionLines: { ...dimensionLines, [selectedDimensionId]: { ...dim, start: newStart, end: newEnd } } });
      },

      startDrawingSurface: (startPos) => {
        const id = uuidv4();
        const newSurface = { id, points: [startPos], fillColor: '#C5E2C5', fillOpacity: 0.5, hatchPattern: HATCH_PATTERNS.NONE, hatchColor: '#000000', hatchScale: 1 };
        set({ drawing: true, activeSurfaceId: id, surfaces: { ...get().surfaces, [id]: newSurface }, tempEnd: startPos });
      },
      addPointToSurface: (pos) => {
        const { activeSurfaceId, surfaces } = get();
        if (!activeSurfaceId || !surfaces[activeSurfaceId]) return;
        const surface = surfaces[activeSurfaceId];
        if (surface.points.length > 2) {
          const startPoint = surface.points[0];
          const dist = Math.sqrt(Math.pow(pos.x - startPoint.x, 2) + Math.pow(startPoint.y - pos.y, 2));
          if (dist < 20) {
            get().finishDrawingSurface();
            return;
          }
        }
        const newPoints = [...surface.points, pos];
        set({ surfaces: { ...surfaces, [activeSurfaceId]: { ...surface, points: newPoints } }, tempEnd: pos });
      },
      finishDrawingSurface: () => {
        const { activeSurfaceId, surfaces } = get();
        if (activeSurfaceId && surfaces[activeSurfaceId].points.length < 3) {
           const newSurfaces = { ...surfaces };
           delete newSurfaces[activeSurfaceId];
           set({ surfaces: newSurfaces, drawing: false, activeSurfaceId: null, tempEnd: null });
        } else {
           set({ drawing: false, tool: TOOLS.SELECT, selectedSurfaceId: activeSurfaceId, activeSurfaceId: null, tempEnd: null });
        }
      },
      updateSurface: (surfaceId, updates) => {
        set((state) => ({ surfaces: { ...state.surfaces, [surfaceId]: { ...state.surfaces[surfaceId], ...updates } } }));
      },
      deleteSurface: (surfaceId) => {
        set((state) => {
          const newSurfaces = { ...state.surfaces };
          delete newSurfaces[surfaceId];
          return { surfaces: newSurfaces, selectedSurfaceId: null };
        });
      },
      startDraggingSurface: (surfaceId, startPos) => {
        set({ isDraggingSurface: true, selectedSurfaceId: surfaceId, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedDimensionId: null, dragStartPos: startPos, draggingSurfacePointIndex: null });
      },
      updateDraggingSurface: (deltaX, deltaY) => {
        const { isDraggingSurface, selectedSurfaceId, surfaces } = get();
        if (!isDraggingSurface || !selectedSurfaceId) return;
        const surface = surfaces[selectedSurfaceId];
        const newPoints = surface.points.map(p => ({ x: p.x + deltaX, y: p.y + deltaY }));
        set({ surfaces: { ...get().surfaces, [selectedSurfaceId]: { ...surface, points: newPoints } } });
      },
      startDraggingSurfacePoint: (surfaceId, pointIndex) => {
        set({ selectedSurfaceId: surfaceId, draggingSurfacePointIndex: pointIndex, isDraggingCorner: false, isDraggingWall: false, isDraggingElement: false, isDraggingSurface: false, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedDimensionId: null });
      },
      updateDraggingSurfacePoint: (newPos) => {
        const { selectedSurfaceId, draggingSurfacePointIndex, surfaces } = get();
        if (!selectedSurfaceId || draggingSurfacePointIndex === null) return;
        const surface = surfaces[selectedSurfaceId];
        if (!surface) return;
        const newPoints = [...surface.points];
        newPoints[draggingSurfacePointIndex] = newPos;
        set({ surfaces: { ...surfaces, [selectedSurfaceId]: { ...surface, points: newPoints } } });
      },

      updateRoomName: (roomId, name) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], name: name } } }));
      },
      updateRoomFillColor: (roomId, color) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], fillColor: color } } }));
      },
      updateRoomFillOpacity: (roomId, opacity) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], fillOpacity: opacity } } }));
      },
      updateRoomWallThickness: (roomId, thickness) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], wallThickness: thickness } } }));
      },
      updateRoomWallColor: (roomId, color) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], wallColor: color } } }));
      },
      updateRoomManualArea: (roomId, area) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], manualArea: area } } }));
      },
      updateRoomHatch: (roomId, hatchUpdates) => {
        set((state) => ({ rooms: { ...state.rooms, [roomId]: { ...state.rooms[roomId], ...hatchUpdates } } }));
      },
      applyRoomPropertiesToWalls: (roomId) => {
        const { rooms, walls, updateWallThickness, updateWallStyle } = get();
        const room = rooms[roomId];
        if (!room) return;
        Object.entries(walls).forEach(([wallId, wall]) => {
          const roomWallsContainWall = room.corners.some(cornerId => wall.startCornerId === cornerId || wall.endCornerId === cornerId);
          if (roomWallsContainWall) {
            if (room.wallThickness) updateWallThickness(wallId, room.wallThickness);
            if (room.wallColor) updateWallStyle(wallId, { color: room.wallColor });
          }
        });
      },

      addNodeToWall: (wallId, point) => {
        const { walls, corners, elements } = get();
        const wall = walls[wallId];
        if (!wall) return;
        const startCorner = corners[wall.startCornerId], endCorner = corners[wall.endCornerId];
        const newCornerId = uuidv4();
        const newCorner = { id: newCornerId, x: point.x, y: point.y };
        const newWallId = uuidv4();
        const newWall = { ...wall, id: newWallId, startCornerId: newCornerId, endCornerId: wall.endCornerId };
        const updatedOriginalWall = { ...wall, endCornerId: newCornerId };
        const wallLength = Math.sqrt(Math.pow(endCorner.x - startCorner.x, 2) + Math.pow(endCorner.y - startCorner.y, 2));
        const splitDist = Math.sqrt(Math.pow(point.x - startCorner.x, 2) + Math.pow(point.y - startCorner.y, 2));
        const splitRatio = splitDist / wallLength;
        const updatedElements = { ...elements };
        Object.values(elements).forEach(element => {
            if (element.wallId === wallId) {
                if (element.param > splitRatio) {
                    const newParam = (element.param - splitRatio) / (1 - splitRatio);
                    updatedElements[element.id] = { ...element, wallId: newWallId, param: Math.max(0, Math.min(1, newParam)) };
                } else {
                    const newParam = element.param / splitRatio;
                    updatedElements[element.id] = { ...element, param: Math.max(0, Math.min(1, newParam)) };
                }
            }
        });
        set(state => ({ corners: { ...state.corners, [newCornerId]: newCorner }, walls: { ...state.walls, [wallId]: updatedOriginalWall, [newWallId]: newWall }, elements: updatedElements, tool: TOOLS.SELECT, selectedCornerId: newCornerId, selectedWallId: null }));
        setTimeout(() => get().detectRooms(), 100);
      },

      deleteWall: (wallId) => {
        set((state) => {
          const wall = state.walls[wallId];
          if (!wall) return state;
          const newWalls = { ...state.walls }, newElements = { ...state.elements }, newCorners = { ...state.corners };
          Object.entries(newElements).forEach(([elementId, element]) => { if (element.wallId === wallId) delete newElements[elementId]; });
          delete newWalls[wallId];
          const isCornerUsed = (cornerId, wallsObj) => Object.values(wallsObj).some(w => w.startCornerId === cornerId || w.endCornerId === cornerId);
          if (!isCornerUsed(wall.startCornerId, newWalls)) delete newCorners[wall.startCornerId];
          if (!isCornerUsed(wall.endCornerId, newWalls)) delete newCorners[wall.endCornerId];
          return { walls: newWalls, elements: newElements, corners: newCorners, selectedWallId: null };
        });
        setTimeout(() => get().detectRooms(), 100);
      },

      deleteRoom: (roomId) => {
        set((state) => {
          const room = state.rooms[roomId];
          if (!room) return state;
          const newRooms = { ...state.rooms }, newWalls = { ...state.walls }, newElements = { ...state.elements }, newCorners = { ...state.corners };
          const roomWallIds = [];
          Object.entries(newWalls).forEach(([wallId, wall]) => { if (room.corners.some(cornerId => wall.startCornerId === cornerId || wall.endCornerId === cornerId)) roomWallIds.push(wallId); });
          Object.entries(newElements).forEach(([elementId, element]) => { if (roomWallIds.includes(element.wallId)) delete newElements[elementId]; });
          roomWallIds.forEach(wallId => delete newWalls[wallId]);
          const isCornerUsed = (cornerId, wallsObj) => Object.values(wallsObj).some(w => w.startCornerId === cornerId || w.endCornerId === cornerId);
          room.corners.forEach(cornerId => { if (!isCornerUsed(cornerId, newWalls)) delete newCorners[cornerId]; });
          delete newRooms[roomId];
          return { rooms: newRooms, walls: newWalls, elements: newElements, corners: newCorners, selectedRoomId: null };
        });
      },
      
      recalculateRoomAreas: () => {
        const { rooms, corners } = get();
        const updatedRooms = { ...rooms };
        let changed = false;
        Object.values(updatedRooms).forEach(room => {
           const area = calculatePolygonArea(room.corners, corners), center = calculatePolygonCenter(room.corners, corners);
           if (Math.abs(area - room.area) > 0.01 || center.x !== room.center.x || center.y !== room.center.y) {
               updatedRooms[room.id] = { ...room, area, center, manualArea: null };
               changed = true;
           }
        });
        if (changed) set({ rooms: updatedRooms });
      },

      updateWallThickness: (wallId, thickness) => {
        set((state) => ({ walls: { ...state.walls, [wallId]: { ...state.walls[wallId], thickness: thickness } } }));
      },
      updateWallStyle: (wallId, style) => {
        set((state) => ({ walls: { ...state.walls, [wallId]: { ...state.walls[wallId], ...style } } }));
      },
      updateWallLength: (wallId, newLength, anchor = 'start') => {
        const { walls, corners } = get();
        const wall = walls[wallId];
        if (!wall || !wall.startCornerId || !wall.endCornerId) return;
        const start = corners[wall.startCornerId], end = corners[wall.endCornerId];
        if (!start || !end) return;
        const dx = end.x - start.x, dy = end.y - start.y, currentLength = Math.sqrt(dx * dx + dy * dy), angle = Math.atan2(dy, dx);
        if (newLength <= 0) return; 
        const diff = newLength - currentLength, ux = Math.cos(angle), uy = Math.sin(angle), newCorners = { ...corners }, movedCorners = new Set();
        const moveCorner = (cornerId, moveX, moveY) => { if (newCorners[cornerId]) { newCorners[cornerId] = { ...newCorners[cornerId], x: newCorners[cornerId].x + moveX, y: newCorners[cornerId].y + moveY }; movedCorners.add(cornerId); } };
        if (anchor === 'start') moveCorner(wall.endCornerId, ux * diff, uy * diff);
        else if (anchor === 'end') moveCorner(wall.startCornerId, -ux * diff, -uy * diff);
        else if (anchor === 'center') { const halfDiff = diff / 2; moveCorner(wall.endCornerId, ux * halfDiff, uy * halfDiff); moveCorner(wall.startCornerId, -ux * halfDiff, -uy * halfDiff); }
        set({ corners: newCorners });
        movedCorners.forEach(cid => get().updateElementsOnWallsWithCorner(cid));
        get().recalculateRoomAreas();
      },
      updateElementWidth: (elementId, width) => {
        set((state) => ({ elements: { ...state.elements, [elementId]: { ...state.elements[elementId], width: width } } }));
      },
      updateElementDepth: (elementId, depth) => {
        set((state) => ({ elements: { ...state.elements, [elementId]: { ...state.elements[elementId], depth: depth } } }));
      },
      
      updateElementColor: (elementId, color) => {
        set((state) => ({ 
          elements: { 
            ...state.elements, 
            [elementId]: { ...state.elements[elementId], fillColor: color } 
          } 
        }));
      },

      updateElementFlip: (elementId, flipType) => {
        set((state) => {
          const element = state.elements[elementId];
          if (!element) return { elements: state.elements };
          return { elements: { ...state.elements, [elementId]: { ...element, flipX: flipType === 'horizontal' ? !element.flipX : element.flipX, flipY: flipType === 'vertical' ? !element.flipY : element.flipY } } };
        });
      },

      duplicateElement: (elementId) => {
        const { elements, walls, corners } = get();
        const element = elements[elementId];
        if (!element) return;
        const wall = walls[element.wallId];
        if (!wall) return;
        const start = corners[wall.startCornerId], end = corners[wall.endCornerId];
        if (!start || !end) return;
        const wallLength = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
        if (wallLength === 0) return;
        const offset = (element.width || 80) + 20, paramOffset = offset / wallLength;
        let newParam = element.param + paramOffset;
        if (newParam > 0.95) newParam = element.param - paramOffset;
        newParam = Math.max(0.001, Math.min(0.999, newParam));
        const newPos = getPositionOnWall(wall, corners, newParam), newId = uuidv4();
        const newElement = { ...element, id: newId, param: newParam, position: newPos };
        set(state => ({ elements: { ...state.elements, [newId]: newElement }, selectedElementId: newId }));
      },

      startDraggingCorner: (cornerId) => {
        const corner = get().corners[cornerId];
        set({ isDraggingCorner: true, selectedCornerId: cornerId, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null, dragStartPos: { x: corner.x, y: corner.y } });
      },
      updateDraggingCorner: (newPos) => {
        const { isDraggingCorner, selectedCornerId } = get();
        if (!isDraggingCorner || !selectedCornerId) return;
        const corner = get().corners[selectedCornerId];
        const updatedCorner = { ...corner, x: newPos.x, y: newPos.y };
        set((state) => ({ corners: { ...state.corners, [selectedCornerId]: updatedCorner } }));
        get().updateElementsOnWallsWithCorner(selectedCornerId);
        get().recalculateRoomAreas();
      },
      startDraggingWall: (wallId) => {
        const wall = get().walls[wallId], startCorner = get().corners[wall.startCornerId], endCorner = get().corners[wall.endCornerId];
        set({ isDraggingWall: true, selectedWallId: wallId, selectedCornerId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null, dragStartPos: { start: { x: startCorner.x, y: startCorner.y }, end: { x: endCorner.x, y: endCorner.y } } });
      },
      updateDraggingWall: (deltaX, deltaY) => {
        const { isDraggingWall, selectedWallId, corners, walls } = get();
        if (!isDraggingWall || !selectedWallId) return;
        const wall = walls[selectedWallId], startCorner = corners[wall.startCornerId], endCorner = corners[wall.endCornerId];
        const updatedStartCorner = { ...startCorner, x: startCorner.x + deltaX, y: startCorner.y + deltaY }, updatedEndCorner = { ...endCorner, x: endCorner.x + deltaX, y: endCorner.y + deltaY };
        set((state) => ({ corners: { ...state.corners, [wall.startCornerId]: updatedStartCorner, [wall.endCornerId]: updatedEndCorner } }));
        get().updateElementsOnWall(selectedWallId);
        get().recalculateRoomAreas();
      },
      startDraggingElement: (elementId) => {
        const element = get().elements[elementId];
        set({ isDraggingElement: true, selectedElementId: elementId, selectedCornerId: null, selectedWallId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null, dragStartPos: element.position, dragStartData: { wallId: element.wallId, param: element.param } });
      },
      updateDraggingElement: (worldPos) => {
        const { isDraggingElement, selectedElementId, elements, walls, corners } = get();
        if (!isDraggingElement || !selectedElementId) return;
        const element = elements[selectedElementId], wall = walls[element.wallId];
        if (!wall) return;
        const { snapInfo } = getClosestWall(worldPos, { [element.wallId]: wall }, corners, 50);
        if (!snapInfo) return;
        const clampedParam = Math.max(0.001, Math.min(0.999, snapInfo.param)), newPos = getPositionOnWall(wall, corners, clampedParam);
        set((state) => ({ elements: { ...state.elements, [selectedElementId]: { ...state.elements[selectedElementId], position: newPos, param: clampedParam, angle: getWallAngle(wall, corners) } } }));
      },
      stopDragging: () => set({ isDraggingCorner: false, isDraggingWall: false, isDraggingElement: false, isDraggingSurface: false, isDraggingDimension: false, draggingSurfacePointIndex: null, dragStartPos: null, dragStartData: null }),

      updateElementsOnWall: (wallId) => {
        const { elements, walls, corners } = get();
        const wall = walls[wallId];
        if (!wall) return;
        const updatedElements = { ...elements };
        Object.entries(elements).forEach(([elementId, element]) => { if (element.wallId === wallId) { const newPos = getPositionOnWall(wall, corners, element.param), newAngle = getWallAngle(wall, corners); updatedElements[elementId] = { ...element, position: newPos, angle: newAngle }; } });
        set({ elements: updatedElements });
      },
      updateElementsOnWallsWithCorner: (cornerId) => {
        const { walls } = get();
        Object.entries(walls).forEach(([wallId, wall]) => { if (wall.startCornerId === cornerId || wall.endCornerId === cornerId) get().updateElementsOnWall(wallId); });
      },

      detectRooms: () => {
        const { walls, corners, rooms: existingRooms } = get();
        const { wallThickness, wallColor, roomFillColor } = usePreferencesStore.getState();
        const detectedRooms = {};
        const findClosedCycles = () => {
          const wallMap = new Map();
          Object.values(walls).forEach(wall => {
            if (!wall.endCornerId) return;
            if (!wallMap.has(wall.startCornerId)) wallMap.set(wall.startCornerId, []);
            if (!wallMap.has(wall.endCornerId)) wallMap.set(wall.endCornerId, []);
            const sn = wallMap.get(wall.startCornerId); if (!sn.includes(wall.endCornerId)) sn.push(wall.endCornerId);
            const en = wallMap.get(wall.endCornerId); if (!en.includes(wall.startCornerId)) en.push(wall.startCornerId);
          });
          const allCycles = [];
          const dfs = (current, start, path, visited) => {
             path.push(current); visited.add(current);
             const neighbors = wallMap.get(current) || [];
             for (const next of neighbors) { if (next === start && path.length >= 3) allCycles.push([...path]); else if (!visited.has(next) && path.length < 50) dfs(next, start, path, visited); }
             visited.delete(current); path.pop();
          };
          Object.keys(corners).forEach(id => dfs(id, id, [], new Set()));
          const uniqueCycles = [], cycleSignatures = new Set();
          allCycles.forEach(cycle => {
             const minId = cycle.reduce((m, id) => id < m ? id : m, cycle[0]), minIdx = cycle.indexOf(minId), rotated = [...cycle.slice(minIdx), ...cycle.slice(0, minIdx)], sigForward = rotated.join('|'), reversed = [...cycle].reverse(), minIdRev = reversed.reduce((m, id) => id < m ? id : m, reversed[0]), minIdxRev = reversed.indexOf(minIdRev), rotatedRev = [...reversed.slice(minIdxRev), ...reversed.slice(0, minIdxRev)], sigReverse = rotatedRev.join('|');
             if (!cycleSignatures.has(sigForward) && !cycleSignatures.has(sigReverse)) { cycleSignatures.add(sigForward); uniqueCycles.push(rotated); }
          });
          return uniqueCycles;
        };
        const getCycleSignature = (cycle) => { const minId = cycle.reduce((m, id) => id < m ? id : m, cycle[0]), minIdx = cycle.indexOf(minId), rotated = [...cycle.slice(minIdx), ...cycle.slice(0, minIdx)]; return rotated.join('|'); };
        const existingRoomMap = new Map(); Object.values(existingRooms).forEach(room => existingRoomMap.set(getCycleSignature(room.corners), room.id));
        findClosedCycles().forEach((cycle, index) => {
          const sig = getCycleSignature(cycle); let roomId = existingRoomMap.get(sig), existingRoomData = roomId ? existingRooms[roomId] : null, area = calculatePolygonArea(cycle, corners), center = calculatePolygonCenter(cycle, corners);
          if (existingRoomData) detectedRooms[roomId] = { ...existingRoomData, area, center, corners: cycle, manualArea: null };
          else { roomId = uuidv4(); detectedRooms[roomId] = { id: roomId, area, center, corners: cycle, name: `Pomieszczenie ${Object.keys(detectedRooms).length + 1}`, fillColor: roomFillColor, fillOpacity: 50, wallThickness, wallColor, hatchPattern: HATCH_PATTERNS.NONE, hatchColor: '#000000', hatchScale: 1 }; }
        });
        set({ rooms: detectedRooms });
      },

      addElement: (elementType, worldPos) => {
        const { tool, elements, corners, walls } = get();
        if (tool !== elementType) return;
        const closestWall = getClosestWall(worldPos, walls, corners, 30);
        if (!closestWall) return;
        const elementId = uuidv4();
        
        let width = 100;
        if (elementType === ELEMENT_TYPES.DOOR) width = 80;
        else if (elementType === ELEMENT_TYPES.DOUBLE_DOOR) width = 140;
        else if (elementType === ELEMENT_TYPES.SLIDING_DOOR) width = 140;
        else if (elementType === ELEMENT_TYPES.GARAGE_DOOR) width = 250;
        
        const newElement = {
          id: elementId,
          type: elementType,
          wallId: closestWall.wall.id,
          position: closestWall.snapInfo.point,
          param: closestWall.snapInfo.param,
          width: width,
          depth: (elementType === ELEMENT_TYPES.SLIDING_DOOR || elementType === ELEMENT_TYPES.OPENING) ? 17 : 15,
          angle: getWallAngle(closestWall.wall, corners),
          flipX: false,
          flipY: false,
          fillColor: '#ffffff'
        };
        set((state) => ({ elements: { ...state.elements, [elementId]: newElement } }));
        get().clearSelection();
      },
      deleteElement: (elementId) => { set((state) => { const newElements = { ...state.elements }; delete newElements[elementId]; return { elements: newElements, selectedElementId: null }; }); },
      startDrawing: (startPos, snapId = null) => {
        const { tool, corners, walls } = get(), { wallThickness, wallColor } = usePreferencesStore.getState();
        if (tool !== TOOLS.WALL) return;
        let startCornerId = snapId; const newCorners = { ...corners };
        if (!startCornerId) { startCornerId = uuidv4(); newCorners[startCornerId] = { id: startCornerId, x: startPos.x, y: startPos.y }; }
        const wallId = uuidv4();
        const wall = { id: wallId, startCornerId, endCornerId: null, thickness: wallThickness, color: wallColor, lineStyle: 'solid' };
        set({ drawing: true, activeWallId: wallId, corners: newCorners, walls: { ...walls, [wallId]: wall }, tempEnd: { x: startPos.x, y: startPos.y } });
      },

      startDrawingRoom: (startPos) => set({ drawing: true, tool: TOOLS.ROOM, drawingStartPos: startPos, tempEnd: startPos }),
      updateDrawingRoom: (currentPos) => set({ tempEnd: currentPos }),
      finishDrawingRoom: (endPos) => {
        const { drawingStartPos, corners, walls } = get(), { wallThickness, wallColor } = usePreferencesStore.getState();
        if (!drawingStartPos) return;
        const sx = drawingStartPos.x, sy = drawingStartPos.y, ex = endPos.x, ey = endPos.y, c1 = uuidv4(), c2 = uuidv4(), c3 = uuidv4(), c4 = uuidv4();
        const newCorners = { ...corners, [c1]: { id: c1, x: sx, y: sy }, [c2]: { id: c2, x: ex, y: sy }, [c3]: { id: c3, x: ex, y: ey }, [c4]: { id: c4, x: sx, y: ey } };
        const w1 = uuidv4(), w2 = uuidv4(), w3 = uuidv4(), w4 = uuidv4(), wp = { thickness: wallThickness, color: wallColor, lineStyle: 'solid' };
        const newWalls = { ...walls, [w1]: { id: w1, startCornerId: c1, endCornerId: c2, ...wp }, [w2]: { id: w2, startCornerId: c2, endCornerId: c3, ...wp }, [w3]: { id: w3, startCornerId: c3, endCornerId: c4, ...wp }, [w4]: { id: w4, startCornerId: c4, endCornerId: c1, ...wp } };
        set({ corners: newCorners, walls: newWalls, drawing: false, drawingStartPos: null, tempEnd: null, tool: TOOLS.SELECT });
        setTimeout(() => get().detectRooms(), 100);
      },

      updateDrawing: (currentPos) => { if (get().drawing) set({ tempEnd: currentPos }); },
      finishDrawing: (endPos, snapId = null) => {
        const { activeWallId, walls, corners } = get(), { wallThickness, wallColor } = usePreferencesStore.getState();
        if (!activeWallId) return;
        let endCornerId = snapId, newCorners = { ...corners }, isClosingLoop = false;
        if (snapId) { let curr = activeWallId; const visited = new Set(); while (curr && !visited.has(curr)) { visited.add(curr); const w = walls[curr]; if (!w) break; if (w.startCornerId === snapId) { isClosingLoop = true; break; } const prev = Object.values(walls).find(p => p.endCornerId === w.startCornerId); if (!prev) break; curr = prev.id; } }
        if (!endCornerId) { endCornerId = uuidv4(); newCorners[endCornerId] = { id: endCornerId, x: endPos.x, y: endPos.y }; }
        const updatedWall = { ...walls[activeWallId], endCornerId };
        if (isClosingLoop) { set({ corners: newCorners, walls: { ...walls, [activeWallId]: updatedWall }, drawing: false, activeWallId: null, tempEnd: null, tool: TOOLS.SELECT }); setTimeout(() => get().detectRooms(), 100); }
        else { const nwId = uuidv4(), nw = { id: nwId, startCornerId: endCornerId, endCornerId: null, thickness: wallThickness, color: wallColor, lineStyle: 'solid' }; set({ corners: newCorners, walls: { ...walls, [activeWallId]: updatedWall, [nwId]: nw }, activeWallId: nwId, tempEnd: { x: endPos.x, y: endPos.y } }); }
      },
      cancelDrawing: () => {
        const { activeWallId, walls, activeSurfaceId, surfaces, tool, drawingStartPos } = get();
        if (tool === TOOLS.ROOM || (tool === TOOLS.DIMENSION_LINE && drawingStartPos)) set({ drawing: false, drawingStartPos: null, tempEnd: null });
        else if (activeWallId) { const nw = { ...walls }; delete nw[activeWallId]; set({ drawing: false, activeWallId: null, tempEnd: null, walls: nw }); }
        else if (activeSurfaceId) { const ns = { ...surfaces }; delete ns[activeSurfaceId]; set({ drawing: false, activeSurfaceId: null, tempEnd: null, surfaces: ns }); }
      },
      resetScene: () => set({ corners: {}, walls: {}, elements: {}, rooms: {}, surfaces: {}, dimensionLines: {}, drawing: false, activeWallId: null, activeSurfaceId: null, drawingStartPos: null, selectedCornerId: null, selectedWallId: null, selectedElementId: null, selectedRoomId: null, selectedSurfaceId: null, selectedDimensionId: null, isDraggingCorner: false, isDraggingWall: false, isDraggingElement: false, isDraggingSurface: false, isDraggingDimension: false })
    }),
    {
      name: 'floorcraft-scene-storage',
      partialize: (state) => ({ projectName: state.projectName, projectId: state.projectId, corners: state.corners, walls: state.walls, elements: state.elements, rooms: state.rooms, surfaces: state.surfaces, dimensionLines: state.dimensionLines, scale: state.scale, position: state.position }), 
    }
  )
);
export default useSceneStore;